import { useRef, useState } from "react";
import { Upload, Loader2, X, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { adminUploadFile, formatApiError } from "@/lib/api";

/**
 * Hybrid image/file picker.
 * - "Upload" tab: pick a file from disk → uploads to backend → returns public URL.
 * - "URL" tab: paste any external URL (kept for backward-compat with existing seeded products).
 */
export const ImageUpload = ({ value, onChange, accept = "image/*,application/pdf", testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mode, setMode] = useState("upload"); // 'upload' | 'url'

  const isPdf = (value || "").toLowerCase().endsWith(".pdf");

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("File too large (max 6 MB)");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const res = await adminUploadFile(file, setProgress);
      onChange(res.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-1 bg-secondary p-1 rounded-full w-fit">
        <button
          type="button"
          onClick={() => setMode("upload")}
          className={`text-xs font-medium px-3 py-1 rounded-full transition ${
            mode === "upload"
              ? "bg-white text-primary shadow-sm"
              : "text-muted-foreground"
          }`}
          data-testid={`${testId}-tab-upload`}
        >
          Upload
        </button>
        <button
          type="button"
          onClick={() => setMode("url")}
          className={`text-xs font-medium px-3 py-1 rounded-full transition ${
            mode === "url"
              ? "bg-white text-primary shadow-sm"
              : "text-muted-foreground"
          }`}
          data-testid={`${testId}-tab-url`}
        >
          URL
        </button>
      </div>

      {mode === "upload" ? (
        <div
          className="border-2 border-dashed border-border rounded-xl p-4 bg-secondary/30 hover:border-primary/40 transition cursor-pointer"
          onClick={() => !uploading && inputRef.current?.click()}
          onDrop={(e) => {
            e.preventDefault();
            if (uploading) return;
            const f = e.dataTransfer.files?.[0];
            if (f) handleFile(f);
          }}
          onDragOver={(e) => e.preventDefault()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            data-testid={`${testId}-input`}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <div className="flex items-center gap-3">
            {uploading ? (
              <Loader2 className="w-5 h-5 text-primary animate-spin shrink-0" />
            ) : (
              <Upload className="w-5 h-5 text-primary shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {uploading
                  ? `Uploading… ${progress}%`
                  : "Click or drop file here"}
              </p>
              <p className="text-xs text-muted-foreground">
                JPG, PNG, WebP, or PDF · max 6 MB
              </p>
            </div>
          </div>
        </div>
      ) : (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="h-10 rounded-lg"
          data-testid={`${testId}-url-input`}
        />
      )}

      {value && (
        <div className="flex items-center gap-3 p-2 bg-white border border-border rounded-xl">
          {isPdf ? (
            <span className="w-12 h-12 grid place-items-center bg-secondary rounded-lg shrink-0">
              <FileText className="w-5 h-5 text-primary" />
            </span>
          ) : (
            <img
              src={value}
              alt="preview"
              className="w-12 h-12 object-cover rounded-lg shrink-0 bg-secondary"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground truncate" title={value}>
              {value}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-destructive"
            onClick={() => onChange("")}
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
